import type { Node } from "prosemirror-model";
import { NodeSelection } from "prosemirror-state";
import type { EditorView, NodeView } from "prosemirror-view";

import { isImageNode } from "../adapter/schema";

type HandleCorner = "top-left" | "top-right" | "bottom-left" | "bottom-right";

// Matches --page-default-max-width. Kept as a constant rather than reading the CSS variable
// at runtime to avoid getComputedStyle overhead on every pointermove during drag.
const MAX_IMAGE_WIDTH_PX = 660;

function clampWidth(raw: number): number {
  return Math.round(Math.min(MAX_IMAGE_WIDTH_PX, Math.max(20, raw)));
}

const POSITION_STYLES: Record<HandleCorner, string> = {
  "top-left": "-top-1.5 -left-1.5",
  "top-right": "-top-1.5 -right-1.5",
  "bottom-right": "-bottom-1.5 -right-1.5",
  "bottom-left": "-bottom-1.5 -left-1.5",
};

const HANDLE_CONFIGS: { corner: HandleCorner; cursor: string }[] = [
  { corner: "top-left", cursor: "nwse-resize" },
  { corner: "top-right", cursor: "nesw-resize" },
  { corner: "bottom-right", cursor: "nwse-resize" },
  { corner: "bottom-left", cursor: "nesw-resize" },
];

/**
 * ProseMirror NodeView for images, providing resize handles for authors.
 *
 * Resize UX behavior:
 * - Left-side handles (top-left, bottom-left): drag right shrinks the image
 * - Right-side handles (top-right, bottom-right): drag right enlarges the image
 * - Direct DOM mutation during drag; single AttrStep dispatched on pointerup
 *
 * Uses pointer events + setPointerCapture so drag works on touch/stylus devices
 * and events are routed to the handle without document-level listeners.
 * Uses selectNode/deselectNode to show/hide handles, and ignoreMutation to
 * prevent ProseMirror from treating live style changes as content edits.
 */
export class ImageView implements NodeView {
  dom: HTMLSpanElement;

  private img: HTMLImageElement;

  private resizeHandles: HTMLDivElement;

  private view: EditorView;

  private getPos: () => number | undefined;

  private isAuthoringPage: () => boolean;

  // Drag state
  private isDragging = false;

  private dragIsLeftSide = false;

  private dragStartX = 0;

  private dragStartWidth = 0;

  constructor(
    node: Node,
    view: EditorView,
    getPos: () => number | undefined,
    isAuthoringPage: () => boolean,
  ) {
    this.view = view;
    this.getPos = getPos;
    this.isAuthoringPage = isAuthoringPage;

    this.dom = document.createElement("span");
    this.dom.className = "image-wrapper";

    this.img = document.createElement("img");
    this.img.className = "inline-block align-bottom";
    this.applyNodeAttrs(node);

    // stopEvent returns true for click on this.img when in authoring mode, so
    // ProseMirror won't handle it there. We dispatch a NodeSelection ourselves
    // to ensure the image is selected and the resize handles are shown, even when
    // clicking from an unfocused state.
    this.img.addEventListener("click", () => {
      if (!this.isAuthoringPage()) return;
      const pos = this.getPos();
      if (pos !== undefined) {
        this.view.dispatch(
          this.view.state.tr.setSelection(NodeSelection.create(this.view.state.doc, pos)),
        );
      }
    });

    this.resizeHandles = this.createResizeHandles();

    this.dom.appendChild(this.img);
    this.dom.appendChild(this.resizeHandles);
  }

  private applyNodeAttrs(node: Node): void {
    if (!isImageNode(node)) return;
    const { src, width } = node.attrs;
    this.img.src = src;
    if (width) {
      this.img.style.width = `${width}px`;
    } else {
      this.img.style.width = "";
    }
  }

  private createResizeHandles(): HTMLDivElement {
    const resizeHandles = document.createElement("div");
    resizeHandles.className = "image-resize-handles";

    for (const handleConfig of HANDLE_CONFIGS) {
      const handle = document.createElement("div");
      handle.className = `pointer-events-auto absolute box-border size-3 touch-none border border-[#777] bg-white opacity-80 ${POSITION_STYLES[handleConfig.corner]}`;
      handle.dataset.corner = handleConfig.corner;
      handle.style.cursor = handleConfig.cursor;

      handle.addEventListener("pointerdown", (e: PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        handle.setPointerCapture(e.pointerId);
        this.startResize(e, handleConfig.corner.includes("left"));
      });

      handle.addEventListener("pointermove", (e: PointerEvent) => {
        if (!this.isDragging) return;
        const deltaX = e.clientX - this.dragStartX;
        const raw = this.dragIsLeftSide
          ? this.dragStartWidth - deltaX // left handle: drag right = shrink
          : this.dragStartWidth + deltaX; // right handle: drag right = grow
        this.img.style.width = `${clampWidth(raw)}px`;
      });

      handle.addEventListener("pointerup", (e: PointerEvent) => {
        if (!this.isDragging) return;
        this.stopResize();

        const deltaX = e.clientX - this.dragStartX;
        const newWidth = clampWidth(
          this.dragIsLeftSide ? this.dragStartWidth - deltaX : this.dragStartWidth + deltaX,
        );

        const pos = this.getPos();
        if (pos !== undefined) {
          const tr = this.view.state.tr.setNodeAttribute(pos, "width", newWidth);
          this.view.dispatch(tr);
        }
      });

      // No-op after normal pointerup (isDragging already false). On abnormal cancellation,
      // reverts the DOM width so it stays consistent with what other users see.
      handle.addEventListener("lostpointercapture", () => {
        if (!this.isDragging) return;
        this.stopResize();
        this.img.style.width = `${this.dragStartWidth}px`;
      });

      resizeHandles.appendChild(handle);
    }

    return resizeHandles;
  }

  update(node: Node): boolean {
    if (!isImageNode(node)) return false; // let ProseMirror handle the node view
    this.applyNodeAttrs(node);
    return true; // update applied; keep this node view
  }

  selectNode(): void {
    this.dom.classList.add("is-selected");
  }

  deselectNode(): void {
    this.dom.classList.remove("is-selected");
  }

  stopEvent(event: Event): boolean {
    // Prevent ProseMirror from handling pointer/mouse events on handles so that
    // the node stays selected and the drag can begin cleanly.
    if (
      (event.type === "pointerdown" || event.type === "mousedown") &&
      event.target instanceof HTMLElement
    ) {
      return "corner" in event.target.dataset;
    }

    // Prevent ProseMirror's click handler from resetting the NodeSelection after
    // mousedown already established it. Without this, clicking the image from an
    // unfocused editor can cause the resize handles to not appear.
    if (event.type === "click" && event.target === this.img && this.isAuthoringPage()) {
      return true;
    }

    return false; // let ProseMirror handle all other events
  }

  // eslint-disable-next-line class-methods-use-this
  ignoreMutation(): boolean {
    // Prevent ProseMirror from treating live style.width changes during drag,
    // or resize handle visibility changes, as document content mutations.
    return true;
  }

  private startResize(e: PointerEvent, isLeftSide: boolean): void {
    this.isDragging = true;
    this.dragIsLeftSide = isLeftSide;
    this.dragStartX = e.clientX;
    this.dragStartWidth = this.img.offsetWidth;

    // Lock cursor globally so it doesn't flicker as the pointer moves over other elements
    document.body.style.cursor = (e.target as HTMLElement).style.cursor;
  }

  private stopResize(): void {
    this.isDragging = false;
    document.body.style.cursor = "";
  }

  destroy(): void {
    // Reset state in case the view is destroyed mid-drag
    this.stopResize();
  }
}

/** Factory for ImageView */
export function createImageNodeView(isAuthoringPage: () => boolean) {
  return (node: Node, view: EditorView, getPos: () => number | undefined) =>
    new ImageView(node, view, getPos, isAuthoringPage);
}
